// CompilerService.ts
import * as Comlink from "comlink";
import {WasmValidator} from './wasmValidator';
import {MotokoPackageManager} from "./motokoPackageManager";
import {motokoErrorCodes} from "../mocErrorCodes";

// Enhanced configuration for compiler service
const SERVICE_CONFIG = {
    WORKER: {
        TIMEOUT: 60000,  // 60 second timeout for initialization
        MEMORY: {
            initialMemoryMB: 4096,    // 4GB initial memory
            maximumMemoryMB: 8192,    // 8GB maximum memory
            stackSizeMB: 256,         // 256MB stack size
        }
    },
    COMPILATION: {
        CHUNK_SIZE: 1024 * 1024,  // 1MB chunks for large files
        MAX_PARALLEL_OPERATIONS: 3,
        GC_THRESHOLD: 0.8,        // 80% memory usage triggers GC
        RETRY_ATTEMPTS: 3,        // Number of retries for failed operations
        RETRY_DELAY: 1000,        // Delay between retries in ms
        THRESHOLDS: {
            FILE_SIZE: 500 * 1024,    // 500KB individual file
            TOTAL_SIZE: 2 * 1024 * 1024, // 2MB total
            IMPORT_COUNT: 20,         // Number of imports
            PACKAGE_COUNT: 5          // Number of packages
        }
    }
};

// Enhanced interfaces
interface CompilationOutput {
    wasm: Uint8Array;
    candid: string | null;
    stable: string | null;
    typescript: string | null;
    didJs: string | null;
    jsonSchema: string | null;
}

interface CompileOptions {
    packages?: Array<{
        name: string;
        repo: string;
        version: string;
        dir?: string | null;
    }>;
    forceDfx?: boolean;  // Option to force dfx compilation
    mode: string;
}

interface CompilationStats {
    startTime: number;
    endTime?: number;
    memoryUsage: number;
    fileCount: number;
    totalSize: number;
    compilationMethod?: 'moc.js' | 'dfx';
}

interface CompileMetrics {
    maxFileSize: number;
    totalSize: number;
    importCount: number;
    packageCount: number;
}

export class CompilerService {
    private worker: Worker | null = null;
    private proxy: any = null;
    private initialized = false;
    private initPromise: Promise<void> | null = null;
    private activeCompilations = 0;
    private compilationStats: CompilationStats[] = [];

    async init(): Promise<void> {
        // console.log('[CompilerService] Init called with state:', {
        //     initialized: this.initialized,
        //     hasWorker: !!this.worker,
        //     hasProxy: !!this.proxy
        // });

        if (this.initialized) {
            console.log('[CompilerService] Already initialized, returning');
            return;
        }
        if (this.initPromise) {
            console.log('[CompilerService] Using existing init promise');
            return this.initPromise;
        }

        console.log('[CompilerService] Creating new init promise');
        this.initPromise = (async () => {
            try {
                console.log('[CompilerService] Creating worker with config:', SERVICE_CONFIG.WORKER);
                this.worker = new Worker(new URL("./moc.worker.ts", import.meta.url), {
                    type: "module",
                    name: "MotokoCompiler",
                    credentials: 'same-origin'
                } as WorkerOptions);

                await new Promise<void>((resolve, reject) => {
                    if (!this.worker) {
                        console.error('[CompilerService] Worker creation failed');
                        return reject(new Error('No worker'));
                    }

                    let timeoutId = setTimeout(() => {
                        console.error('[CompilerService] Worker initialization timed out');
                        reject(new Error('Worker initialization timed out'));
                    }, 30000);

                    const handler = (event: MessageEvent) => {
                        // console.log('[CompilerService] Received worker message:', event.data);
                        if (event.data.type === 'ready') {
                            // console.log('[CompilerService] Worker ready message received');
                            clearTimeout(timeoutId);
                            this.worker?.removeEventListener('message', handler);
                            resolve();
                        }
                    };
                    this.worker.addEventListener('message', handler);
                });

                console.log('[CompilerService] Creating Comlink proxy');
                this.proxy = Comlink.wrap(this.worker);
                this.initialized = true;
                console.log('[CompilerService] Initialization complete');
            } catch (err: unknown) {
                const errorMessage = err instanceof Error ? err.message : String(err);
                console.error('[CompilerService] Initialization failed:', errorMessage);
                this.cleanup();
                throw new Error(`Initialization failed: ${errorMessage}`);
            }
        })();

        try {
            await this.initPromise;
            this.initPromise = null;
        } catch (err: unknown) {
            this.initPromise = null;
            const errorMessage = err instanceof Error ? err.message : String(err);
            throw new Error(`Init promise failed: ${errorMessage}`);
        }
    }

    private async retryOperation<T>(
        operation: () => Promise<T>,
        maxAttempts: number = SERVICE_CONFIG.COMPILATION.RETRY_ATTEMPTS
    ): Promise<T> {
        let lastError: Error | null = null;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                return await operation();
            } catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                if (attempt < maxAttempts) {
                    console.log(`[CompilerService] Retry attempt ${attempt} failed, retrying...`);
                    await new Promise(resolve => setTimeout(resolve, SERVICE_CONFIG.COMPILATION.RETRY_DELAY));
                }
            }
        }

        throw lastError;
    }

    private async processLargeFile(file: { name: string; content: string }): Promise<void> {
        const contentLength = file.content.length;

        if (contentLength > SERVICE_CONFIG.COMPILATION.CHUNK_SIZE) {
            console.log(`[CompilerService] Processing large file ${file.name} in chunks`);
            const chunks = Math.ceil(contentLength / SERVICE_CONFIG.COMPILATION.CHUNK_SIZE);

            for (let i = 0; i < chunks; i++) {
                const start = i * SERVICE_CONFIG.COMPILATION.CHUNK_SIZE;
                const end = Math.min((i + 1) * SERVICE_CONFIG.COMPILATION.CHUNK_SIZE, contentLength);
                const chunk = file.content.slice(start, end);

                await this.retryOperation(async () => {
                    await this.proxy.Moc({
                        type: "save",
                        file: i === 0 ? file.name : `${file.name}.part${i}`,
                        content: chunk
                    });
                });
            }
        } else {
            await this.retryOperation(async () => {
                await this.proxy.Moc({
                    type: "save",
                    file: file.name,
                    content: file.content
                });
            });
        }
    }

    async compileFiles(
        files: { name: string; content: string }[],
        mainFile: string,
        options?: CompileOptions
    ): Promise<CompilationOutput> {
        console.log('[CompilerService] Starting compilation of files:', files.map(f => f.name));
        console.log('[CompilerService] Main file:', mainFile);
        console.log('[CompilerService] Package options:', options?.packages);

        if (!this.initialized) {
            console.log('[CompilerService] Not initialized, calling init()');
            await this.init();
        }

        if (!this.worker || !this.proxy) {
            console.error('[CompilerService] Worker or proxy not available after init');
            throw new Error('Compiler service not properly initialized');
        }

        try {
            // Always initialize package manager and process base package first
            const packageManager = new MotokoPackageManager();

            // Process base package (always required)
            console.log('[CompilerService] Processing base package');
            try {
                // Create base package directory and add package reference
                await this.proxy.Moc({
                    type: "addPackage",
                    name: "base",
                    path: "src/lib/base/"
                });
                console.log('[CompilerService] Registered base package with compiler');

                // Fetch and save base package files
                const baseFiles = await packageManager.getPackageFiles({
                    name: 'base',
                    repo: 'dfinity/motoko-base',
                    version: 'moc-0.14.9',
                    dir: 'src'
                });

                console.log('[CompilerService] Base package files:', Array.from(baseFiles.keys()));

                for (const [path, content] of baseFiles.entries()) {
                    if (path && content) {
                        const fullPath = `src/lib/base/${path}`;
                        console.log(`[CompilerService] Saving base file: ${fullPath}`);
                        await this.proxy.Moc({
                            type: "save",
                            file: fullPath,
                            content
                        });
                    }
                }
            } catch (error) {
                console.error('[CompilerService] Base package processing failed:', error);
                throw new Error(`Base package processing failed: ${error instanceof Error ? error.message : String(error)}`);
            }



            // Process additional packages if provided
            if (options?.packages && options.packages.length > 0) {
                console.log('[CompilerService] Processing additional packages:', options.packages);
                for (const pkg of options.packages) {
                    if (pkg.name === 'base') continue; // Skip base package as it's already processed

                    try {
                        console.log(`[CompilerService] Processing package:`, {
                            name: pkg.name,
                            repo: pkg.repo,
                            version: pkg.version,
                            dir: pkg.dir
                        });

                        await this.proxy.Moc({
                            type: "addPackage",
                            name: pkg.name,
                            path: `src/lib/${pkg.name}/`
                        });
                        console.log(`[CompilerService] Registered package ${pkg.name} with compiler`);

                        const packageFiles = await packageManager.getPackageFiles(pkg);
                        console.log(`[CompilerService] Package ${pkg.name} files:`, Array.from(packageFiles.keys()));

                        for (const [path, content] of packageFiles.entries()) {
                            if (path && content) {
                                const fullPath = `src/lib/${pkg.name}/${path}`;
                                console.log(`[CompilerService] Saving file: ${fullPath}`);
                                await this.proxy.Moc({
                                    type: "save",
                                    file: fullPath,
                                    content
                                });
                            }
                        }

                        console.log(`[CompilerService] Successfully processed package ${pkg.name}`);
                    } catch (error) {
                        console.error(`[CompilerService] Error processing package ${pkg.name}:`, error);
                        throw new Error(`Failed to process package ${pkg.name}: ${error instanceof Error ? error.message : String(error)}`);
                    }
                }
            }

            // Set public metadata
            console.log('[CompilerService] Setting public metadata');
            await this.proxy.Moc({
                type: "setPublicMetadata",
                list: ["candid:service", "candid:args", "motoko:stable-types"]
            });

            // Save all project files
            for (const file of files) {
                console.log(`[CompilerService] Saving file: ${file.name}`);
                await this.proxy.Moc({
                    type: "save",
                    file: file.name,
                    content: file.content
                });
                console.log(`[CompilerService] File saved: ${file.name}`);
            }

            // Check for errors
            console.log(`[CompilerService] Checking main file: ${mainFile}`);
            const check = await this.proxy.Moc({type: "check", file: mainFile});
            console.log('[CompilerService] Check result:', {
                hasCheck: !!check,
                hasDiagnostics: !!check?.diagnostics,
                diagnosticsCount: Array.isArray(check?.diagnostics) ? check.diagnostics.length : 'not an array',
                diagnostics: check?.diagnostics
            });

            if (check?.diagnostics) {
                if (Array.isArray(check.diagnostics)) {
                    // Log warnings (severity 2) but don't fail
                    const warnings = check.diagnostics.filter(d => d && typeof d === 'object' && d.severity === 2);
                    if (warnings.length > 0) {
                        console.warn('[CompilerService] Compilation warnings:',
                            warnings.map(d => d.message).join('\n')
                        );
                    }

                    // Only fail on errors (severity 1)
                    const errors = check.diagnostics.filter(d => d && typeof d === 'object' && d.severity === 1);
                    if (errors.length > 0) {
                        // Create error with diagnostics attached
                        const error = new Error('Compilation check failed');
                        (error as any).diagnostics = check.diagnostics; // Attach all diagnostics, not just errors
                        throw error;  // This error will be caught and propagated up
                    }
                }
            }

            // Generate Candid interface
            console.log('[CompilerService] Generating Candid interface');
            let mocCandid = null;
            try {
                const candidResult = await this.proxy.Moc({
                    type: "candid",
                    file: mainFile
                });

                console.log('[CompilerService] Raw candid result:', candidResult);

                // Extract candid properly from result
                if (typeof candidResult?.code === 'string') {
                    mocCandid = candidResult.code;
                } else if (candidResult?.code?.candid) {
                    mocCandid = candidResult.code.candid;
                } else if (typeof candidResult === 'string') {
                    mocCandid = candidResult;
                }

                // console.log('[CompilerService] Moc.js Candid generation complete:', mocCandid);
            } catch (candidError) {
                console.warn('[CompilerService] Moc.js candid generation failed:', candidError);
            }

            // Compile to WASM
            console.log('[CompilerService] Starting WASM compilation');
            const compileResult = await this.proxy.Moc({
                type: "compile",
                file: mainFile,
                eop_version: 1
            });
            console.log('[CompilerService] WASM compilation complete');

            if (!compileResult?.code?.wasm) {
                if (compileResult?.diagnostics) {
                    const error = new Error('WASM compilation failed');
                    (error as any).diagnostics = compileResult.diagnostics;
                    throw error;
                }
                throw new Error('No WASM output received from compiler');
            }

            const wasmBytes = new Uint8Array(compileResult.code.wasm);

            // Validate WASM output
            console.log('[CompilerService] Validating WASM output');
            const validation = await WasmValidator.validateWasm(wasmBytes);

            if (!validation.isValid) {
                console.error('[CompilerService] Invalid WASM output');
                throw new Error(`Invalid WASM output: ${validation.details}`);
            }

            // Get stable types
            const stableString = compileResult.code?.stable?.toString() || null;

            // If we successfully generated Candid, use the lightweight endpoint
            if (mocCandid && mocCandid.trim().length > 0) {
                console.log('[CompilerService] Using lightweight endpoint for additional artifacts');
                try {
                    // Use AbortController for timeout functionality
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

                    // Call the new lightweight endpoint with just the Candid
                    const response = await fetch('https://dfxutils.coinnation.io/generateArtifactsFromCandid', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            candid: mocCandid
                        }),
                        signal: controller.signal
                    });

                    // Clear the timeout since we got a response
                    clearTimeout(timeoutId);

                    if (!response.ok) {
                        throw new Error(`Lightweight endpoint failed with status: ${response.status}`);
                    }

                    const artifactsResult = await response.json();

                    if (!artifactsResult.success) {
                        throw new Error(`Artifact generation failed: ${artifactsResult.error || 'Unknown error'}`);
                    }

                    console.log('[CompilerService] Successfully generated artifacts with lightweight endpoint');

                    // Return combined results
                    return {
                        wasm: wasmBytes,
                        candid: mocCandid,
                        stable: stableString,
                        typescript: artifactsResult.artifacts.typescript || "",
                        didJs: artifactsResult.artifacts.didJs || "",
                        jsonSchema: artifactsResult.artifacts.jsonSchema || "{}"
                    };
                } catch (artifactsError) {
                    console.warn('[CompilerService] Lightweight endpoint failed, falling back to full compilation:',
                        artifactsError instanceof Error ? artifactsError.message : String(artifactsError));

                    // Fall back to full dfxUtils compilation if the lightweight endpoint fails
                    const dfxResult = await this.callDfxUtils(files, options, mainFile);

                    return {
                        wasm: wasmBytes,
                        candid: mocCandid || dfxResult.candid,
                        stable: stableString,
                        typescript: dfxResult.typescript,
                        didJs: dfxResult.didJs,
                        jsonSchema: dfxResult.jsonSchema
                    };
                }
            } else {
                // No Candid was generated, fall back to the full dfxUtils approach
                console.log('[CompilerService] No Candid generated, using full dfxUtils compilation');
                const dfxResult = await this.callDfxUtils(files, options, mainFile);

                return {
                    wasm: wasmBytes,
                    candid: dfxResult.candid,
                    stable: stableString,
                    typescript: dfxResult.typescript,
                    didJs: dfxResult.didJs,
                    jsonSchema: dfxResult.jsonSchema
                };
            }
        } catch (mocError: unknown) {
            // Handle moc.js errors
            console.error('[CompilerService] Moc.js error:', mocError);

            // Check if it's a known memory error that we should fall back for
            if (Array.isArray(mocError)) {
                const [code, errType, errCode] = mocError;
                console.log('[CompilerService] Error details:', {code, errType, errCode});

                // Check specifically for stack overflow or memory errors
                if (code === 248 || errType === 'Stack_overflow' || errCode === -9) {
                    console.log('[CompilerService] Moc.js compilation failed with memory error, trying dfx fallback');
                    try {
                        console.log('[CompilerService] Attempting dfxUtils fallback');
                        const dfxResult = await this.callDfxUtils(files, options, mainFile);

                        if (!dfxResult.wasm) {
                            throw new Error('DFX compilation failed to produce WASM');
                        }

                        // Convert base64 WASM to Uint8Array
                        const binaryString = atob(dfxResult.wasm);
                        const wasmBytes = new Uint8Array(binaryString.length);
                        for (let i = 0; i < binaryString.length; i++) {
                            wasmBytes[i] = binaryString.charCodeAt(i);
                        }

                        // Validate WASM
                        const validation = await WasmValidator.validateWasm(wasmBytes);
                        if (!validation.isValid) {
                            throw new Error(`Invalid WASM output from DFX: ${validation.details}`);
                        }

                        console.log('[CompilerService] Successfully compiled with dfxUtils fallback');
                        return {
                            wasm: wasmBytes,
                            candid: dfxResult.candid,
                            stable: null,
                            typescript: dfxResult.typescript,
                            didJs: dfxResult.didJs,
                            jsonSchema: dfxResult.jsonSchema
                        };
                    } catch (dfxError) {
                        console.error('[CompilerService] DFX fallback failed:', dfxError);
                        throw new Error(`DFX fallback failed: ${dfxError instanceof Error ? dfxError.message : String(dfxError)}`);
                    }
                }
            }

            // If it's not a memory error we recognize, rethrow
            const compilationError = new Error(
                `Compilation failed: ${mocError instanceof Error ? mocError.message : String(mocError)}`
            );

            if (mocError && typeof mocError === 'object' && 'diagnostics' in mocError) {
                (compilationError as any).diagnostics = (mocError as any).diagnostics;
            }

            throw compilationError;
        }
    }


    async callDfxUtils(
        files: { name: string; content: string }[],
        options?: CompileOptions,
        mainFile?: string  // Add this parameter
    ) {
        try {
            // Log what we're sending
            console.log('[CompilerService] Calling dfxUtils with:', {
                filesCount: files.length,
                packages: options?.packages,
                mode: options?.mode
            });

            // const response = await fetch('http://localhost:4946/compile', {
            //     method: 'POST',
            //     headers: {
            //         'Content-Type': 'application/json',
            //     },
            //     body: JSON.stringify({
            //         files,
            //         packages: options?.packages  // Add packages here
            //     }),
            // });

            const response = await fetch('https://dfxutils.coinnation.io/compile', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    files,
                    packages: options?.packages,
                    mode: options?.mode || 'reinstall',
                    mainFile: mainFile
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to compile with dfxUtils');
            }

            const result = await response.json();
            return {
                wasm: result.wasm,
                candid: result.candid,
                typescript: result.typescript,
                didJs: result.didJs,
                jsonSchema: result.jsonSchema
            };
        } catch (error) {
            console.error('DfxUtils compilation failed:', error);
            throw error;
        }
    }


    private getMemoryUsage(): number {
        try {
            return (globalThis as any)?.performance?.memory?.usedJSHeapSize || 0;
        } catch {
            return 0;
        }
    }


    async mocCommand(command: any): Promise<any> {
        if (!this.initialized) {
            await this.init();
        }
        if (!this.proxy) {
            throw new Error('Compiler service not properly initialized');
        }

        return await this.retryOperation(async () => {
            const result = await this.proxy.Moc(command);

            if (command.type === 'check' && result?.diagnostics) {
                try {
                    if ('diagnostics' in result) {
                        // Transform diagnostics but preserve severity
                        return {
                            ...result,
                            diagnostics: {
                                ...result.diagnostics,
                                diagnostic: this.transformDiagnostics(result.diagnostics.diagnostic)
                            }
                        };
                    }
                } catch (error) {
                    console.error('[CompilerService] Error transforming diagnostics:', error);
                }
            }

            return result;
        });
    }


    private transformDiagnostics(diagnostics: any) {
        if (!Array.isArray(diagnostics) && diagnostics) {
            diagnostics = [diagnostics];
        }
        if (!diagnostics) return [];

        return diagnostics
            .map(d => {
                if (!d || !d.message) return null;

                const range = d.range || {
                    start: {line: 0, character: 0},
                    end: {line: 0, character: 0}
                };

                let message = d.message;
                if (d.code && motokoErrorCodes[d.code]) {
                    message = `${message}\n\nExplanation: ${motokoErrorCodes[d.code]}`;
                }

                // Keep original severity (1 for error, 2 for warning)
                return {
                    severity: d.severity || 1,
                    message: message,
                    code: d.code || '',
                    source: d.source || 'motoko',
                    range: {
                        start: {
                            line: range.start.line || 0,
                            character: range.start.character || 0
                        },
                        end: {
                            line: range.end.line || 0,
                            character: range.end.character || 0
                        }
                    }
                };
            })
            .filter(d => d !== null);
    }

    cleanup() {
        console.log('[CompilerService] Cleaning up resources');
        if (this.worker) {
            if (this.proxy?.cleanup) {
                this.proxy.cleanup().catch((error: Error) => {
                    console.error('[CompilerService] Error during proxy cleanup:', error);
                });
            }
            this.worker.terminate();
            this.worker = null;
            this.proxy = null;
            this.initialized = false;
            this.initPromise = null;
            this.activeCompilations = 0;
            this.compilationStats = [];
        }
        console.log('[CompilerService] Cleanup complete');
    }

    getCompilationStats(): CompilationStats[] {
        return this.compilationStats;
    }
}